import React, { useMemo, useState } from 'react'
import { useHistory, generatePath } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { IPost, IPostOrder } from 'interfaces'
import { TDeletePostProps } from 'services/CommunityService'
import { TBlockUserProps, TUnblockUserProps } from 'services/NetworkService'
import { CardSize } from 'enums'
import { APP_URL, BREAKPOINTS } from 'config/app'
import { CardContainer } from 'layout'
import { Context, ContextType } from 'containers/Community/Context'
import { useFetchCommunity, useFetchPostLikes } from 'containers/Community/hooks'
import { useMutationBlocks } from 'containers/Network/hooks'
import { useInvalidateProfile } from 'containers/User/hooks'
import { useFetchOrder } from 'containers/Market/hooks'
import { PostLikeUserCard, PostCheck } from 'containers/Community/components'
import {
    unsubscribeCommunity,
    unsubscribeCompany,
    unsubscribeStore,
    unsubscribeStoreUser,
    unsubscribeUser,
} from 'containers/Network/network-actions'
import {
    Loader,
    Button,
    Modal,
    List,
} from 'components'
import { formDataInitial } from 'forms/PostForm/PostForm'
import { PostAction } from 'form-actions'
import { useWindowResize, useAlertDialog } from 'hooks'
import { PostDto } from 'dto'
import styleOverlay from 'styles/modules/overlay.module.css'
import { useMutationPost } from './hooks'

type CommunityPropType = {
    onLikeCompletePost?: (post: IPost) => void
    onEditCompletePost?: (post: IPost) => void
    onDeleteCompletePost?: (post: IPost) => void
    onBlockUserComplete?: (value: string) => void
    onError?: (value: string) => void
}

const Community: React.FC<CommunityPropType> = ({
    children,
    onLikeCompletePost = () => {},
    onEditCompletePost = () => {},
    onDeleteCompletePost = () => {},
    onBlockUserComplete = () => {},
    onError = () => {},
}) => {
    const { t } = useTranslation()
    const history = useHistory()
    const dispatch = useDispatch()
    const [windowWidth] = useWindowResize()
    const { showAlertDialog } = useAlertDialog()

    const [postProps, setPostProps] = useState(formDataInitial)
    const [postData, setPostData] = useState<IPost>()
    const [postOrderData, setPostOrderData] = useState<IPostOrder>()

    const [isOpenModalPostLikes, setIsOpenModalPostLikes] = useState(false)
    const [isOpenModalPostForm, setIsOpenModalPostForm] = useState(false)
    const [isOpenModalCheck, setIsOpenModalCheck] = useState(false)

    const [context, setContext] = useState<ContextType>({
        isLoading: false,
        onLikePost: handlerLikePost,
        onLikesPost: handlerLikesPost,
        onEditPost: handlerEditPost,
        onDeletePost: handlerDeletePost,
        onUnsubscribeUser: handlerUnsubscribePostUser,
        onUnsubscribeUserInStore: handlerUnsubscribeUserInStore,
        onUnsubscribeStore: handlerUnsubscribePostStore,
        onUnsubscribeCommunity: handlerUnsubscribePostCommunity,
        onUnsubscribeCompany: handlerUnsubscribePostCompany,
        onBlockUser: handlerBlockPostUser,
        onShowChek: handlerShowChek,
    })

    const { data: dataCommunity } = useFetchCommunity({
        can_publish: true,
    }, {
        enabled: isOpenModalPostForm,
    })

    const {
        isInitialLoading: isLoadingPostLikes,
        data: dataPostLikes,
    } = useFetchPostLikes({
        id: postData?.id ?? 0,
    }, {
        enabled: isOpenModalPostLikes && !!postData,
    })

    const {
        isInitialLoading: isLoadingCheck,
        data: dataOrder,
    } = useFetchOrder({
        storeId: postOrderData?.store_id ?? 0,
        orderId: postOrderData?.id ?? 0,
    }, {
        enabled: isOpenModalCheck && !!postOrderData,
    })

    const { deletePost, likePost } = useMutationPost()

    const { blockUser, unblockUser } = useMutationBlocks()

    const { invalidate: invalidateProfile } = useInvalidateProfile()

    const itemPostLikeSize = useMemo(() => {
        if (windowWidth > BREAKPOINTS.tabletLandscape) {
            return CardSize.thirdWidth
        }
        return CardSize.halfWidth
    }, [windowWidth])

    const handlerCloseModalPostForm = () => {
        setIsOpenModalPostForm(false)
    }

    const handlerCloseModalPostLikes = () => {
        setIsOpenModalPostLikes(false)
    }

    const handlerCloseModalCheck = () => {
        setIsOpenModalCheck(false)
    }

    const handlerEditCompletePost = (post: IPost) => {
        onEditCompletePost(post)
        handlerCloseModalPostForm()
    }

    const handlerAddOrderToBasket = () => {
        if (postOrderData) {
            const { id: orderId, store_id: storeId } = postOrderData
            history.push(generatePath(APP_URL.basketFromOrder, { id: storeId, orderId }))
        }
    }

    function handlerLikePost(post: IPost) {
        likePostAction(post)
    }

    function handlerLikesPost(post: IPost) {
        setPostData(post)
        setIsOpenModalPostLikes(true)
    }

    function handlerEditPost(post: IPost) {
        setPostData(post)
        setPostProps({ ...formDataInitial, ...PostDto.getPostProps(post) })
        setIsOpenModalPostForm(true)
    }

    function handlerDeletePost(post: IPost, message: string = '') {
        showAlertDialog({
            message,
            payload: { post },
            onConfirm: removePost,
        })
    }

    function handlerUnsubscribePostUser(userId: number, message: string = '') {
        showAlertDialog({
            message,
            payload: { userId },
            onConfirm: unsubscribePostUser,
        })
    }

    function handlerUnsubscribeUserInStore(userId: number, storeId: number, message: string = '') {
        showAlertDialog({
            message,
            payload: { storeId, userId },
            onConfirm: unsubscribeUserInStore,
        })
    }

    function handlerUnsubscribePostStore(storeId: number, message: string = '') {
        showAlertDialog({
            message,
            payload: { storeId },
            onConfirm: unsubscribePostStore,
        })
    }

    function handlerUnsubscribePostCommunity(communityId: number, message: string = '') {
        showAlertDialog({
            message,
            payload: { communityId },
            onConfirm: unsubscribePostCommunity,
        })
    }

    function handlerUnsubscribePostCompany(companyId: number, message: string = '') {
        showAlertDialog({
            message,
            payload: { companyId },
            onConfirm: unsubscribePostCompany,
        })
    }

    function handlerBlockPostUser(userId: number, message: string) {
        showAlertDialog({
            message,
            payload: { userId },
            onConfirm: blockPostUser,
        })
    }

    function handlerShowChek(order: IPostOrder) {
        setPostOrderData(order)
        setIsOpenModalCheck(true)
    }

    function removePost({ post }: { post: IPost }) {
        const { id, ...props } = PostDto.getPostProps(post)

        if (id) {
            deletePostAction({ id, ...props })
        }
    }

    function unsubscribePostUser({ userId }: { userId: number }) {
        unsubscribeUserAction(userId)
    }

    function unsubscribeUserInStore({ storeId, userId }: { storeId: number, userId: number }) {
        unsubscribeStoreUserAction(storeId, userId)
    }

    function unsubscribePostStore({ storeId }: { storeId: number }) {
        unsubscribePostStoreAction(storeId)
    }

    function unsubscribePostCommunity({ communityId }: { communityId: number }) {
        unsubscribeCommunityAction(communityId)
    }

    function unsubscribePostCompany({ companyId }: { companyId: number }) {
        unsubscribePostCompanyAction(companyId)
    }

    function blockPostUser({ userId }: { userId: number }) {
        blockPostUserAction({ userId })
    }

    function likePostAction(post: IPost) {
        setContext((prevState) => ({ ...prevState, isLoading: true }))
        likePost.mutate({ id: post.id }, {
            onSuccess: (res) => {
                if (res) {
                    // TODO invalidate post in cache
                    onLikeCompletePost({ ...post, ...res })
                }
            },
            onSettled: () => {
                setContext((prevState) => ({ ...prevState, isLoading: false }))
            },
        })
    }

    function deletePostAction(params: TDeletePostProps) {
        deletePost.mutate({ ...params, isPublished: false }, {
            onSuccess: (res) => {
                if (res) {
                    onDeleteCompletePost(res)
                }
            },
        })
    }

    function unsubscribeUserAction(userId: number) {
        dispatch(unsubscribeUser({ userId }))
    }

    function unsubscribeStoreUserAction(storeId: number, userId: number) {
        dispatch(unsubscribeStoreUser({ storeId, userId }))
    }

    function unsubscribePostStoreAction(storeId: number) {
        dispatch(unsubscribeStore({ storeId }))
    }

    function unsubscribeCommunityAction(communityId: number) {
        dispatch(unsubscribeCommunity({ communityId }))
    }

    function unsubscribePostCompanyAction(companyId: number) {
        dispatch(unsubscribeCompany({ companyId }))
    }

    function blockPostUserAction(params: TBlockUserProps) {
        blockUser.mutate(params, {
            onSuccess: (res) => {
                invalidateProfile(params)
                onBlockUserComplete(res)
            },
            onError: ([errorText]) => {
                onError(errorText)
            },
        })
    }

    // TODO
    // eslint-disable-next-line
    function unblockUserAction(params: TUnblockUserProps) {
        unblockUser.mutate(params, {
            onSuccess: (res) => {
                // invalidateProfile()
                // showAlertNotify({ type: 'success', message: res })
            },
            onError: ([errorText]) => {
                // showAlertNotify({ type: 'error', message: errorText })
            },
        })
    }

    return (
        <>
            <Context.Provider value={context}>
                {children}
            </Context.Provider>

            <Modal
                isOpen={isOpenModalPostForm}
                size="medium"
                classesOverlay={styleOverlay.overlay_light}
                onClose={handlerCloseModalPostForm}
            >
                <Modal.Header
                    titlePos="left"
                    title={t('ui_posts_edit_post_title')}
                    isCloseButton
                />
                <PostAction
                    isActiveCommunity={false}
                    postProps={postProps}
                    communities={dataCommunity}
                    onComplete={handlerEditCompletePost}
                />
            </Modal>

            <Modal
                isOpen={isOpenModalPostLikes}
                size="medium"
                onClose={handlerCloseModalPostLikes}
            >
                <Modal.Header
                    isCloseButton
                    titlePos="left"
                    title={`${t('likes')} ${dataPostLikes?.length || ''}`}
                />
                <Modal.Body background>
                    {isLoadingPostLikes && (
                        <Loader />
                    )}
                    {!isLoadingPostLikes && dataPostLikes && (
                        <CardContainer>
                            <List limit={12}>
                                {dataPostLikes.map((item) => (
                                    <PostLikeUserCard
                                        data={item}
                                        size={itemPostLikeSize}
                                        key={item.id}
                                    />
                                ))}
                            </List>
                        </CardContainer>
                    )}
                </Modal.Body>
            </Modal>

            <Modal
                isOpen={isOpenModalCheck}
                size="medium"
                classesOverlay={styleOverlay.overlay_light}
                onClose={handlerCloseModalCheck}
            >
                <Modal.Header
                    titlePos="center"
                    title={t('market_summary_title')}
                    isCloseButton
                />
                <Modal.Body>
                    {isLoadingCheck && (
                        <Loader />
                    )}
                    {!isLoadingCheck && dataOrder && postOrderData && (
                        <PostCheck
                            data={dataOrder}
                            postOrder={postOrderData}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        size="size44"
                        onClick={handlerAddOrderToBasket}
                    >
                        {t('copy_order_to_basket')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default Community
